import { zodResolver } from '@hookform/resolvers/zod';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  getRecordEntries,
  getRecordKeys,
  getRecordValues,
} from '@ipfabric/utils/types';
import { unsafeAssert } from '@ipfabric/utils/types/unsafeAssert';
import { produce } from 'immer';
import type React from 'react';
import { type ReactNode, useMemo } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { useStableCallback } from '../../hooks/useStableCallback';
import { useCustomForm } from '../hookForm/useCustomForm';
import type { Awaitable } from '../types/Awaitable';
import type { FormSnippet } from './FormSnippet';
import type { FormSnippetGroup } from './FormSnippetGroup';
import type { GroupsFieldValues } from './SnippetsFieldValues';
import { collectObjectKeys } from './snippetSchemaKeys';

type AnySnippetGroup<TModel, TGroupDisplayControl> = FormSnippetGroup<
  TModel,
  string,
  TGroupDisplayControl
>;

type Props<
  TModel,
  TGroupKey extends string,
  TGroupDisplayControl,
  TGroups extends Record<
    TGroupKey,
    AnySnippetGroup<TModel, TGroupDisplayControl>
  >,
> = {
  initialModel: TModel;
  // TGroups is there to infer individual snippet field value types.
  // Record<TGroupKey, ...> is there to infer TGroupKey properly.
  groups: TGroups &
    Record<TGroupKey, AnySnippetGroup<TModel, TGroupDisplayControl>>;
  onSubmit?: (model: TModel) => Awaitable<unknown>;
} & (TGroupDisplayControl extends void
  ? { groupDisplayControl?: never }
  : {
      groupDisplayControl: TGroupDisplayControl;
    });

export type FormCompositionHook<
  TGroupKey extends string,
  TGroups extends Record<TGroupKey, any> = Record<TGroupKey, any>,
> = {
  renderGroup: (groupKey: TGroupKey) => ReactNode;
  form: UseFormReturn<GroupsFieldValues<TGroups>>;
  /**
   * Like `useForm().handleSubmit`, it can be used either in <form onSubmit>
   * or triggered manually without arguments to invoke form submission.
   */
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;

  makeHandleSubmit: MakeHandleSubmitFn;
};

export type MakeHandleSubmitFn = (
  onValid?: () => void,
  onInvalid?: () => void,
) => (e?: React.BaseSyntheticEvent) => Promise<void>;

/**
 * Call all snippet hooks in a stable order and return their results
 *
 * Type generics are weak there on purpose - type consistency is checked
 * in individual snippets. TGroupDisplayControl must be kept generic,
 * due to the generic-driven optional show property on snippets it can't
 * be even short-circuited with `any`.
 */
const useSnippetHooks = <TGroupDisplayControl>(
  groups: Record<string, AnySnippetGroup<any, TGroupDisplayControl>>,
  form: UseFormReturn<FieldValues, any, undefined>,
  groupDisplayControl?: TGroupDisplayControl,
) => {
  const orderedSnippets: [groupKey: string, snippetKey: string][] =
    useMemo(() => {
      const result: [string, string][] = [];
      const sortedGroups = getRecordEntries(groups).sort(([a], [b]) =>
        a.localeCompare(b),
      );
      for (const [groupKey, group] of sortedGroups) {
        const sortedSnippets = Object.keys(group.snippets)
          .filter((snippetKey) => group.snippets[snippetKey]?.use !== undefined)
          .sort();
        for (const snippetKey of sortedSnippets) {
          result.push([groupKey, snippetKey]);
        }
      }
      return result;
    }, [groups]);

  const snippetHookResults: Record<string, Record<string, any>> = {};
  for (const [groupKey, snippetKey] of orderedSnippets) {
    snippetHookResults[groupKey] ??= {};
    // presence guaranteed by orderedSnippets factory
    snippetHookResults[groupKey]![snippetKey] = groups[groupKey]!.snippets[
      snippetKey
    ]!.use!({ form, groupDisplayControl });
  }

  return snippetHookResults;
};

/**
 * Instantiate and maintain a react hook form using form snippets for individual input
 *
 * This component orchestrates `FieldValues` of every snippet from every group
 * into a single react-hook-form instance, including their validation and
 * transformation schemes.
 *
 *
 * @example
 * // minimum working setup:
 * type Model = { foo: string };
 *
 * const foo: FormSnippet<Model, { foo: string }> = {
 *   render: ({ form }) => (
 *     <FormTextField control={form.control} type='text' name='foo' />
 *   ),
 *   fromModel: (model, fv) => {
 *     fv.foo = model.foo;
 *   },
 *   toModel: (values, model) => {
 *     model.foo = values.foo;
 *   },
 *   schema: z.object({ foo: z.string() }),
 * };
 *
 * const bar = createFormSnippetGroup({
 *   snippets: {
 *     foo: foo,
 *   },
 *   render: () => null,
 * });
 *
 * const initialModel = { foo: 'hello' };
 *
 * const MyForm = (props: { onSubmit: (model: Model) => void }) => {
 *   const formComposition = useFormComposition({
 *     initialModel,
 *     groups: { bar },
 *     onSubmit: props.onSubmit,
 *   });
 *   return (
 *     <div className='m'>
 *       {formComposition.renderGroup('bar')}
 *       <Button
 *         variant='primary'
 *         onClick={() => formComposition.handleSubmit()}
 *       >
 *         Save
 *       </Button>
 *     </div>
 *   );
 * };
 */
export const useFormComposition = <
  TModel,
  TGroupKey extends string,
  TGroupDisplayControl = void,
  TGroups extends Record<
    TGroupKey,
    AnySnippetGroup<TModel, TGroupDisplayControl>
  > = Record<TGroupKey, AnySnippetGroup<TModel, TGroupDisplayControl>>,
>(
  xprops: Props<TModel, TGroupKey, TGroupDisplayControl, TGroups>,
): FormCompositionHook<TGroupKey, TGroups> => {
  // On the outside we need TGroups to be generic to make FieldValues inference work,
  // i.e. formComposition.form.getValues() to be typed properly.
  // However, the generic starts to overcomplicate types inside this hook, where the
  // code is universal, agnostic to particular FieldValues, so we "un-generic" TGroups
  // with this assertion.
  const props = xprops as Props<
    TModel,
    TGroupKey,
    TGroupDisplayControl,
    Record<TGroupKey, AnySnippetGroup<TModel, TGroupDisplayControl>>
  >;
  const { initialModel } = props;
  const modelToFieldValues = (model: TModel): FieldValues => {
    const draft = {};
    for (const group of getRecordValues(props.groups)) {
      for (const snippet of getRecordValues(group.snippets)) {
        snippet.fromModel(model, draft);
      }
    }
    return draft;
  };

  /** Convert values transformed by zod resolver to model */
  const transformedValuesToModel = (
    initialModel: TModel,
    transformedValues: unknown,
  ): TModel =>
    produce(initialModel, (draft) => {
      for (const group of getRecordValues(props.groups)) {
        for (const snippet of getRecordValues(group.snippets)) {
          snippet.toModel(transformedValues, draft);
        }
      }
    });

  // biome-ignore lint/correctness/useExhaustiveDependencies: initial values are initialized once and cached in the hook form.
  const defaultValues = useMemo(() => modelToFieldValues(initialModel), []);

  const validations = useMemo(() => {
    let schema: z.Schema = z.object({}); // Start with an empty schema
    const seenKeys = new Set<string>();
    const validateSnippetSchema = (
      snippet: FormSnippet<TModel, any, any, any, any>,
    ) => {
      const keys = collectObjectKeys(snippet.schema as z.ZodTypeAny);
      if (!keys) {
        throw new Error(
          `Snippet schema must be a ZodObject, eventually wrapped inside ZodEffects<Object> / ZodUnion / ZodDiscriminatedUnion, got: ${snippet.schema.constructor.name}`,
        );
      }
      for (const key of keys) {
        if (seenKeys.has(key)) {
          throw new Error(
            `Duplicate field "${key}" found in form snippets. Field keys must be unique across all snippets.`,
          );
        }
        seenKeys.add(key);
      }
    };

    for (const group of getRecordValues(props.groups)) {
      for (const snippet of getRecordValues(group.snippets)) {
        // Check for overlapping keys
        validateSnippetSchema(snippet);
        schema = z.intersection(schema, snippet.schema);
      }
    }
    return schema;
  }, [props.groups]);

  /**
   * Run custom (non-zod) validation on snippet if there is any,
   * otherwise just return true
   */
  const isSnippetCustomValid = (
    transformedValues: unknown,
    groupKey: TGroupKey,
    snippetKey: string,
  ) =>
    props.groups[groupKey]!.snippets[snippetKey]!.validate?.(
      transformedValues,
      snippetHookResults[groupKey]?.[snippetKey],
    ) ?? true;

  const form = useCustomForm({
    defaultValues,
    resolver: zodResolver(
      validations.refine((transformedValues) => {
        for (const [groupKey, group] of getRecordEntries(props.groups)) {
          for (const snippetKey of getRecordKeys(group.snippets)) {
            if (
              !isSnippetCustomValid(transformedValues, groupKey, snippetKey)
            ) {
              return false;
            }
          }
        }
        return true;
      }),
    ),
  });

  const snippetHookResults = useSnippetHooks(
    props.groups,
    form,
    props.groupDisplayControl,
  );

  /**
   * On tabbed and similar forms, we need to show a section with an error
   * after a failed submission attempt.
   *
   * This function finds the first group with invalid snippets and
   * calls a callback to show it.
   *
   * We invoke all zod validations there again, as it allows us to
   * map errors to the proper group using an already available group
   * definition - mapping from form resolver error back to snippet
   * would probably require additional implementation in every snippet.
   */
  const showFirstInvalidGroup =
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Still reasonable. All attempts to satisfy led to far more complex code.
    () => {
      const values = form.getValues();
      for (const [groupKey, group] of getRecordEntries(props.groups)) {
        for (const [snippetKey, snippet] of getRecordEntries(group.snippets)) {
          const result = snippet.schema.safeParse(values);
          if (
            !result.success ||
            !isSnippetCustomValid(result, groupKey, snippetKey)
          ) {
            if (props.groupDisplayControl && group.show) {
              group.show(props.groupDisplayControl);
            }
            snippet.showValidationError?.(
              snippetHookResults[groupKey]?.[snippetKey],
            );
            return;
          }
        }
      }
      stumble('Cannot match form composition error to any snippet');
    };

  const makeHandleSubmit = useStableCallback(
    (onValid?: () => void, onInvalid?: () => void) =>
      form.handleSubmit(
        async (data) => {
          // Save form state at the moment of submission to make it as a last "clean" state once save succeeds
          const submittedValues = form.getValues();

          await props.onSubmit?.(
            transformedValuesToModel(props.initialModel, data),
          );

          // Mark submitted values as not dirty now.
          form.reset(submittedValues, { keepDirtyValues: true });

          // Call the custom onValid callback
          onValid?.();
        },
        () => {
          showFirstInvalidGroup();
          // Call the custom onInvalid callback if provided
          onInvalid?.();
        },
      ),
  );

  const handleSubmit = useStableCallback(makeHandleSubmit());

  return {
    renderGroup: (groupKey: TGroupKey) => {
      // Group guaranteed to be there if TGroupKey is correctly inferred. Will fail anyway on render if not present.
      const group = props.groups[groupKey]!;
      if (!group) {
        throw new Error(`Group with key "${groupKey}" not found.`);
      }
      return group.render({
        renderSnippet: (snippetKey) => {
          const snippet = group.snippets[snippetKey];
          if (!snippet) {
            throw new Error(
              `Snippet with key "${snippetKey}" not found in group "${groupKey}".`,
            );
          }
          return snippet.render({
            form,
            hook: snippetHookResults[groupKey]?.[snippetKey],
            groupDisplayControl: props.groupDisplayControl,
          });
        },
        form,
      });
    },
    form: unsafeAssert<
      UseFormReturn,
      UseFormReturn<GroupsFieldValues<TGroups>>
    >(form),
    handleSubmit,
    makeHandleSubmit,
  };
};
